import React, { useEffect, useState } from 'react'
import { fetchData } from '../../../data'
import { Title, Link2, getAuthorDetailsList } from '../../'
import BlogArticleCard from './BlogArticleCard/BlogArticleCard.jsx'
import { unpackAdvisorDocumentsForBlogArticles } from './blogArticleUtilities'
import { removeSpecialCharacters } from '../../../utility/strings/strings.js'
import './blogSimpleArticleList.css'
import { isLocal } from '../../../../global/environments'
import '../../../../components/EmailSubscriptionCenter/Modal'
import {
  getLocalPath,
  getMargins,
  useTaxonomies,
} from '../../../utility/index.js'
import { isInstitutional } from '../../../../global/authentication/authentication-manager.js'

const removeIdIndex = (documents) => {
  return Object.keys(documents).map((documentKey) => {
    return documents[documentKey]
  })
}

const SUBSCRIBE_BTN_STYLE = {
  Primary: 'Primary',
  RPrimary: 'Reverse Primary',
  Tertiary: 'Tertiary',
  RTertiary: 'Reverse Tertiary',
}

export function BlogArticleList({ page, component }) {
  const taxonomies = useTaxonomies(false, ['investor-goals'])
  const parameters = component.getParameters()
  const allInsightsLink =
    parameters.allInsightsLink ||
    (isInstitutional()
      ? '/institutional/insights/latest-insights'
      : '/insights/latest-insights')

  const [items, setItems] = useState([])
  useEffect(() => {
    const { scope } = parameters
    if (scope) {
      const pageSize = 3
      const topic = removeSpecialCharacters(parameters.topic || '')
      let sortField = parameters.sortField || 'cti:publicationDate'
      if (parameters.sortOrder === 'desc') sortField = `-${sortField}`
      const itemsUrl = topic
        ? `${getLocalPath()}/halapi/documents?_scope=${scope}&_expr=jcr:contains(@hippotaxonomy:keys,'${topic}')&_sort=${sortField}&_limit=${pageSize}`
        : `${getLocalPath()}/halapi/documents?_scope=${scope}&_sort=${sortField}&_limit=${pageSize}`

      fetchData(itemsUrl).then((data) => {
        const documents = unpackAdvisorDocumentsForBlogArticles(
          data['_embedded']?.['documents']
        ).reduce((acc, document) => {
          const { id } = document._meta
          const imagePaths = document.imageUrl.split('/')
          const lastImagePath = imagePaths[imagePaths.length - 1]
          acc[id] = {
            document: {
              ...document,
              getData: () => {
                return {
                  title: document.title,
                  name: document.path,
                  publicationDate: document.date,
                  cardSummary: document.cardSummary,
                  time: document.time,
                  types: document.types,
                }
              },
            },
            processedFields: {
              id: id,
              imageUrl: `${document?.imageUrl}/${lastImagePath}/brxp%3alarge`,
              keys: document.keys,
              path: document._meta.path,
              authors: getAuthors(document.authors),
            },
          }
          return acc
        }, {})
        setItems(removeIdIndex(documents))
      })
    }
  }, [page, component])

  const backgroundImage = `${isLocal() ? '/cti-site/binaries' : '/binaries'}${
    parameters?.backgroundImage
  }`

  function getAuthors(authors) {
    const authorlist = authors
      ?.filter((item) => item?._meta?.mirror?.id)
      ?.map((author) => {
        const id = author._meta.mirror.id
        return {
          $ref: `/page/u${id.replaceAll('-', '')}`,
        }
      })

    const authorDetailsList = getAuthorDetailsList(page, authorlist)

    const list = (authorDetailsList || []).map((author) => {
      const { image: { image = '' } = {} } = author
      const imageUrl = page.getContent(image?.$ref)?.getOriginal()?.getUrl()
      return {
        ...author,
        imageUrl,
      }
    })
    return list || []
  }

  return (
    <div
      className={`BlogArticleList article-list u-posRelative ${getMargins(
        parameters.marginTop || 'Large',
        parameters.marginBottom || 'Large'
      )} ${parameters?.backgroundImage ? 'background u-paddingVert5gu' : ''}`}
      style={
        parameters?.backgroundImage
          ? { backgroundImage: 'url("' + backgroundImage + '")' }
          : {}
      }
    >
      <div className="u-sizeConstrained">
        {parameters.title && (
          <div className="u-flex u-flexCol u-sizeFull u-flexAlignItemsStart">
            <Title
              textColor={`${parameters?.backgroundImage ? 'u-textColorWhite' : 'u-textColorBrand'}`}
              text={parameters.title}
            />
          </div>
        )}

        {items.length === 0 ? (
          <div className="no-data-view u-flex u-flexAlignItemsCenter u-flexJustifyCenter">
            <h3>
              There is no Insight available{' '}
              {parameters.topic ? `for "${parameters.topic}" topic` : ''}
            </h3>
          </div>
        ) : (
          <div>
            <div className="blog-articles u-flex u-flexWrap u-sm-flexCol u-flexJustifyBetween u-marginTop5gu">
              {items.map((item) => {
                return (
                  <BlogArticleCard
                    key={item.processedFields.id}
                    item={item}
                    taxonomies={taxonomies}
                    hightlightOnHover
                  />
                )
              })}
            </div>
          </div>
        )}

        <div
          className={
            parameters.subscription
              ? 'u-flex u-flexCol u-flexAlignItemsCenter'
              : ''
          }
        >
          {parameters.subscription && (
            <div
              className={`u-marginBottom4gu
                  ${
                    parameters?.subscriptionButtonStyle ===
                    SUBSCRIBE_BTN_STYLE.Primary
                      ? 'primary u-bgColorBrandDark'
                      : ''
                  }
                  ${
                    parameters?.subscriptionButtonStyle ===
                    SUBSCRIBE_BTN_STYLE.RPrimary
                      ? 'r-primary u-bgColorLight'
                      : ''
                  }
                  ${
                    parameters?.subscriptionButtonStyle ===
                    SUBSCRIBE_BTN_STYLE.Tertiary
                      ? 'tertiary'
                      : ''
                  }
                  ${
                    parameters?.subscriptionButtonStyle ===
                    SUBSCRIBE_BTN_STYLE.RTertiary
                      ? 'r-tertiary'
                      : ''
                  }
                  `}
            >
              <email-subscription-modal
                data-use-unlabeled-button-inst="true"
                data-subscription-name={parameters.subscription}
                data-subscribe-custom-text={
                  parameters.subscribeText || 'Subscribe To Latest Insights'
                }
                data-unsubscribe-custom-text={
                  parameters.subscribeText || 'Subscribed To Latest Insights'
                }
                tertiary
              />
            </div>
          )}

          {allInsightsLink && (
            <Link2
              url={allInsightsLink}
              text="View all insights"
              data-analytics="view-all-insights-link"
              arrow={parameters?.backgroundImage ? 'white' : 'brand'}
              className={`Link u-posRelative hover-underline-animation ${
                parameters?.backgroundImage ? 'u-textColorWhite' : ''
              }`}
              textClasses={`u-textSecondary u-textKernLoose`}
            />
          )}
        </div>
      </div>
    </div>
  )
}
